import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Chess} from 'chess.js';
import {classify} from '@/Screens/ChessAnalysis/analysisParity';
import {sanitizeFen, whitePovCpFromResult} from '../utils/studyHelpers';
import {
  authoredLineExhausted,
  computePracticeOutcome,
  studentPlyOffset,
  type PracticeOutcome,
} from '../utils/practiceOutcome';
import {PRACTICE_VERDICT_COPY} from '../constants/practiceVerdicts';
import type {PracticeGoal, PracticeVerdictStatus} from '../types';

/**
 * Owns everything about one "Practice with Computer" attempt.
 *
 * The important design change vs the previous inline implementation: a
 * practice attempt keeps its OWN linear move list and never writes into the
 * chapter's authored move tree. That one decision fixes three separate bugs
 * at once:
 *
 *  - `addMoveToTree` used to reject any move played from the chapter start
 *    that differed from the authored first move ('blocked'), which deadlocked
 *    the engine before move 1 on any Black-orientation chapter that had
 *    authored moves;
 *  - Retry only moved the selection back to the start, so attempt 2 grew as
 *    variations hanging off attempt 1 and "Your line" showed them merged;
 *  - rewinding with the back arrow re-triggered the engine from an earlier
 *    position and grew yet another branch, making takebacks unlimited and
 *    invisible.
 *
 * The authored tree is still read (it's the reference line for chapters with
 * no explicit goal - see `authoredLineExhausted`), just never written.
 */

export type PracticeAttemptMove = {
  id: string;
  ply: number;
  san: string;
  /** WorkbookMovesList reads `move`; keep it in the tree-node shape. */
  move: string;
  fen: string;
  lastFen: string;
  newFen: string;
  byStudent: boolean;
  child_moves: never[];
  isPracticeMove: true;
};

type UsePracticeGameArgs = {
  /**
   * Whether a game is currently armed. Deliberately NOT "is this a practice
   * chapter": the same hook drives the authored Exercise (auto-armed from the
   * chapter) and the ad-hoc Sandbox (armed by the viewer from any position),
   * so the caller decides what "active" means.
   */
  isActive: boolean;
  /**
   * Identity of the current game. Changing it abandons the attempt and starts
   * a fresh one — a chapter switch for an Exercise, a re-arm for a Sandbox.
   * Also namespaces the attempt nodes' ids.
   */
  sessionKey: string;
  /** Position the game starts from — the chapter start, or wherever the
   * viewer armed the Sandbox. */
  startFen: string;
  /** The colour the human is playing. */
  studentColor: 'w' | 'b';
  practiceGoal: PracticeGoal | null | undefined;
  /** The chapter's authored mainline (flat top-level array). */
  authoredTree: {san?: string}[] | undefined;
  /** Whatever the board is currently showing. */
  moveSelected: any;
  setmoveSelected: (move: any) => void;
  /** Latest engine result, plus the FEN it was actually searched for. */
  evalResult: {score: string; isMate: boolean; fen?: string} | null;
  /** Top-line best move for the current position, from the analysis engine. */
  nextBestMoveUci: string | null;
  /** The opponent engine's chosen reply for the current position. */
  opponentBestMoveUci: string | null;
  /** False while the student has the engine switched off. */
  engineEnabled: boolean;
  /**
   * How many take backs this attempt is allowed, or null for unlimited. The
   * coach's policy used to be enforced only by disabling the panel button,
   * which left every other route to `takeBack` (and any future one) unbounded;
   * the limit belongs with the state it protects.
   */
  takebacksAllowed?: number | null;
  /**
   * Seeds the latched outcome so a viewer who already completed this
   * Exercise in a previous session sees that result immediately on load
   * instead of a blank "ongoing" attempt - without it, the goal only ever
   * looked completed for the remainder of the session it was reached in.
   * Ignored once any move is played this session (reset/appendMove both
   * overwrite the latch); undefined/null behaves exactly as before.
   */
  initialOutcome?: PracticeOutcome | null;
};

export function usePracticeGame({
  isActive,
  sessionKey,
  startFen,
  studentColor,
  practiceGoal,
  authoredTree,
  moveSelected,
  setmoveSelected,
  evalResult,
  nextBestMoveUci,
  opponentBestMoveUci,
  engineEnabled,
  takebacksAllowed = null,
  initialOutcome = null,
}: UsePracticeGameArgs) {
  const [attemptMoves, setAttemptMoves] = useState<PracticeAttemptMove[]>([]);
  const [verdict, setVerdict] = useState<{
    status: PracticeVerdictStatus;
    message: string;
  }>({status: 'idle', message: ''});
  const [takebackCount, setTakebackCount] = useState(0);
  // Identifies *this* attempt, so callers can tell two attempts apart even
  // when they look identical from the outside. Recording used to key off
  // (chapter, status, moveCount), which silently swallowed a second attempt
  // that ended the same way in the same number of moves - a real undercount
  // now that `attempts` is a number a coach reads.
  const [attemptNonce, setAttemptNonce] = useState(0);
  const [isEngineThinking, setIsEngineThinking] = useState(false);
  // Snapshot of the pre-move eval + best move, taken the instant a student
  // move is applied, so the post-move eval (which arrives asynchronously once
  // the engine re-searches) can be classified against it.
  const pendingEvalRef = useRef<{
    afterFen: string;
    lastScoreUser: number;
    bestUci: string | null;
    playedUci: string;
  } | null>(null);

  const resolvedStartFen = sanitizeFen(startFen);

  // --- derived attempt state -------------------------------------------
  const sans = useMemo(() => attemptMoves.map(m => m.san), [attemptMoves]);
  const studentSans = useMemo(
    () => attemptMoves.filter(m => m.byStudent).map(m => m.san),
    [attemptMoves],
  );
  const studentMoveCount = studentSans.length;
  const tipMove = attemptMoves.length
    ? attemptMoves[attemptMoves.length - 1]
    : null;
  const currentFen = tipMove?.fen || resolvedStartFen;

  // The position the student's most recent move produced. The eval-driven
  // goals are judged against THIS, not against whatever is on the board now -
  // see the StudentEval docs in practiceOutcome.ts.
  const lastStudentFen = useMemo(() => {
    for (let i = attemptMoves.length - 1; i >= 0; i--) {
      if (attemptMoves[i].byStudent) {
        return attemptMoves[i].fen;
      }
    }
    return null;
  }, [attemptMoves]);

  // The engine only ever replies to the tip of the attempt. While the student
  // is browsing back through "Your line", it stays quiet - which is what stops
  // a rewind from spawning a second, parallel game.
  const isAtTip = tipMove
    ? moveSelected?.id === tipMove.id
    : !moveSelected?.id || moveSelected?.id === 'start';

  /**
   * The engine's verdict on the position the student's last move produced,
   * student POV — RETAINED rather than read live.
   *
   * Two separate things were losing this eval, and the goals that depend on it
   * (mate / equalize / advantage) need both fixed:
   *
   *  - it used to be matched against `currentFen`, so the opponent's reply
   *    (a few hundred ms) invalidated it long before a depth-24 search on the
   *    student's move had finished; and
   *  - the analysis engine ABANDONS the student's position the instant that
   *    reply lands and restarts on the new one, so `evalResult` is simply
   *    overwritten and the number is gone for good.
   *
   * Keeping the last eval we saw for this position (evals for a given FEN
   * arrive in increasing depth order, so the last one is the deepest we got)
   * means the goal is judged on the best read the engine actually managed,
   * whenever it arrives, instead of on whatever happened to be in flight
   * during a 350ms window. Cleared whenever the student moves again.
   */
  const [studentEval, setStudentEval] = useState<{
    cp: number;
    isMate: boolean;
  } | null>(null);

  useEffect(() => {
    setStudentEval(null);
  }, [lastStudentFen]);

  useEffect(() => {
    if (!evalResult || !lastStudentFen) {
      return;
    }
    const evalFen = evalResult.fen;
    if (!evalFen || sanitizeFen(evalFen) !== sanitizeFen(lastStudentFen)) {
      return;
    }
    const cpWhite = whitePovCpFromResult(evalResult);
    setStudentEval({
      cp: studentColor === 'w' ? cpWhite : -cpWhite,
      isMate: Boolean(evalResult.isMate),
    });
  }, [evalResult, lastStudentFen, studentColor]);

  const reachedEndOfAuthoredLine = useMemo(
    () =>
      authoredLineExhausted(
        authoredTree,
        studentSans,
        studentPlyOffset(resolvedStartFen, studentColor),
      ),
    [authoredTree, studentSans, resolvedStartFen, studentColor],
  );

  const liveOutcome: PracticeOutcome = useMemo(() => {
    if (!isActive) {
      return {status: 'ongoing', reason: ''};
    }
    return computePracticeOutcome({
      startFen: resolvedStartFen,
      sans,
      studentSans,
      studentColor,
      goal: practiceGoal,
      studentEval,
      studentMoveCount,
      reachedEndOfAuthoredLine,
    });
  }, [
    isActive,
    resolvedStartFen,
    sans,
    studentSans,
    studentColor,
    practiceGoal,
    studentEval,
    studentMoveCount,
    reachedEndOfAuthoredLine,
  ]);

  /**
   * Terminal outcomes latch until the attempt is explicitly restarted.
   *
   * `liveOutcome` is derived from a live engine eval, so it is not monotonic:
   * an `advantage` win at +1.6 reverts to 'ongoing' when the next depth
   * reports +1.4. That made the end card flicker in and out, and - because the
   * recording effect keys on (status, moveCount) - let a single attempt be
   * written to the coach's roster more than once. A finished game stays
   * finished.
   */
  const [latchedOutcome, setLatchedOutcome] = useState<PracticeOutcome | null>(
    initialOutcome ?? null,
  );
  // Read fresh inside the sessionKey effect below without making that effect
  // re-fire on every render initialOutcome's identity changes (it's built
  // fresh from a query response in the caller).
  const initialOutcomeRef = useRef(initialOutcome);
  useEffect(() => {
    initialOutcomeRef.current = initialOutcome;
  }, [initialOutcome]);
  useEffect(() => {
    if (liveOutcome.status !== 'ongoing') {
      setLatchedOutcome(prev => prev ?? liveOutcome);
    }
  }, [liveOutcome]);
  const outcome = latchedOutcome ?? liveOutcome;

  // --- applying moves ---------------------------------------------------
  // Mirrors attemptMoves so appendMove can build the next node synchronously
  // (it has to hand the same object to setmoveSelected) without reading a
  // stale `attemptMoves` out of its own closure.
  const attemptMovesRef = useRef<PracticeAttemptMove[]>([]);
  useEffect(() => {
    attemptMovesRef.current = attemptMoves;
  }, [attemptMoves]);

  const appendMove = useCallback(
    (args: {san: string; fen: string; lastFen: string; byStudent: boolean}) => {
      const ply = attemptMovesRef.current.length + 1;
      const node: PracticeAttemptMove = {
        // Ply-scoped rather than FEN-scoped: a repeated position inside one
        // attempt must still be two distinct, separately-selectable nodes.
        id: `practice_${sessionKey}_${ply}`,
        ply,
        san: args.san,
        move: args.san,
        fen: args.fen,
        lastFen: args.lastFen,
        newFen: args.fen,
        byStudent: args.byStudent,
        child_moves: [],
        isPracticeMove: true,
      };
      attemptMovesRef.current = [...attemptMovesRef.current, node];
      setAttemptMoves(attemptMovesRef.current);
      setmoveSelected(node);
    },
    [sessionKey, setmoveSelected],
  );

  /**
   * A move the student played on the board. Returns false when the move is
   * refused - the caller re-syncs the board so it snaps back instead of
   * staying desynced on a move the attempt never accepted.
   *
   * Refused when: the attempt is over, the student is browsing history rather
   * than standing on the tip, or the move belongs to the ENGINE's colour.
   * That last one is what stops a student from dragging the computer's pieces
   * or squeezing a second move in while the reply is still being searched.
   */
  const playStudentMove = useCallback(
    (args: {san: string; newFen: string; lastFen: string}): boolean => {
      if (outcome.status !== 'ongoing' || !isAtTip) {
        return false;
      }
      const moverColor = sanitizeFen(args.lastFen).split(' ')[1];
      if (moverColor !== studentColor) {
        return false;
      }

      // Snapshot the pre-move eval so the verdict can be classified once the
      // engine reports on the resulting position.
      if (engineEnabled && evalResult) {
        try {
          const probe = new Chess(sanitizeFen(args.lastFen));
          const moveObj = probe.move(args.san);
          if (moveObj) {
            const cpWhite = whitePovCpFromResult(evalResult);
            pendingEvalRef.current = {
              afterFen: args.newFen,
              lastScoreUser: studentColor === 'w' ? cpWhite : -cpWhite,
              bestUci: nextBestMoveUci,
              playedUci: `${moveObj.from}${moveObj.to}${
                moveObj.promotion || ''
              }`,
            };
          }
        } catch (e) {
          pendingEvalRef.current = null;
        }
        setVerdict({status: 'idle', message: ''});
      }

      appendMove({
        san: args.san,
        fen: args.newFen,
        lastFen: args.lastFen,
        byStudent: true,
      });
      return true;
    },
    [
      outcome.status,
      isAtTip,
      studentColor,
      engineEnabled,
      evalResult,
      nextBestMoveUci,
      appendMove,
    ],
  );

  /**
   * Classifies the student's last move once the engine reports on the
   * resulting position. Call from the engine's result handler with the FEN
   * the engine actually searched (NOT the currently-selected FEN — those
   * differ in the window between a move landing and the search restarting,
   * which is exactly when this runs).
   */
  const ingestEngineResult = useCallback(
    (result: {score: string; isMate: boolean} | null, searchedFen: string) => {
      if (!isActive || !result) {
        return;
      }
      const pending = pendingEvalRef.current;
      if (!pending) {
        return;
      }
      if (sanitizeFen(searchedFen) !== sanitizeFen(pending.afterFen)) {
        return;
      }
      const cpWhite = whitePovCpFromResult(result);
      const userScore = studentColor === 'w' ? cpWhite : -cpWhite;
      const cls = classify({
        player: 'me',
        score: userScore,
        scoreChange: userScore - pending.lastScoreUser,
        lastScore: pending.lastScoreUser,
      });

      let status: PracticeVerdictStatus;
      if (cls.blunder) {
        status = 'blunder';
      } else if (cls.mistake) {
        status = 'mistake';
      } else if (cls.inaccuracy) {
        status = 'inaccuracy';
      } else if (pending.bestUci && pending.playedUci === pending.bestUci) {
        status = 'best';
      } else {
        status = 'good';
      }
      setVerdict({status, message: PRACTICE_VERDICT_COPY[status]});
      pendingEvalRef.current = null;
    },
    [isActive, studentColor],
  );

  // --- the computer's reply ---------------------------------------------
  const engineToMove =
    isActive &&
    outcome.status === 'ongoing' &&
    isAtTip &&
    sanitizeFen(currentFen).split(' ')[1] !== studentColor;

  // A reply is "pending" from the moment it's the engine's turn until the
  // move actually lands, so the UI can say so and the board can refuse input.
  useEffect(() => {
    setIsEngineThinking(Boolean(engineToMove && engineEnabled));
  }, [engineToMove, engineEnabled]);

  // The engine's latest candidate, read at the moment the timer fires rather
  // than captured when it was scheduled. See the effect below for why.
  const opponentUciRef = useRef<string | null>(null);
  useEffect(() => {
    opponentUciRef.current = opponentBestMoveUci;
  }, [opponentBestMoveUci]);

  // `opponentBestMoveUci` deliberately does NOT appear in this effect's
  // dependencies — only whether one exists at all does.
  //
  // A search reports a new principal variation at every depth, and its first
  // move changes whenever the engine changes its mind. With the UCI itself as
  // a dependency, each of those revisions tore the effect down and restarted
  // the settle delay, so on an unstable position the reply could be deferred
  // for the engine's whole movetime budget - and the default strength for
  // every chapter authored before `practice_difficulty` existed is Maximum
  // (depth 18 / 1500ms), where the top move churns constantly. That is the
  // "computer just doesn't move" report: the timer was being reset faster than
  // it could fire. Now the delay is scheduled once per position and reads
  // whatever the engine's current best move is when it elapses.
  const hasOpponentMove = Boolean(opponentBestMoveUci);

  // How long the reply waits. The analysis engine only searches the student's
  // move until the reply lands (after that it restarts on the new position and
  // never returns to it), so this delay is also the entire budget the
  // eval-driven goals get to judge that move. 350ms left them reading a
  // depth-4-ish guess; the goals that actually depend on a number get a longer
  // look. Deliberately a fixed, unconditional delay rather than "wait until an
  // eval arrives" — a reply that waits on the engine is a reply that can hang
  // if the engine never reports, which is the failure mode being fixed here.
  const goalNeedsEval =
    practiceGoal?.type === 'mate' ||
    practiceGoal?.type === 'equalize' ||
    practiceGoal?.type === 'advantage';
  const replyDelayMs = goalNeedsEval ? 900 : 350;

  useEffect(() => {
    if (!engineToMove || !engineEnabled || !hasOpponentMove) {
      return undefined;
    }
    // Settle delay so the reply doesn't land the same frame as the student's
    // move; the opponent engine's own movetime budget (see
    // practiceDifficulty.ts) is what actually bounds how long this takes.
    const timer = setTimeout(() => {
      const uci = opponentUciRef.current;
      if (!uci || uci.length < 4) {
        return;
      }
      try {
        const chess = new Chess(sanitizeFen(currentFen));
        const from = uci.slice(0, 2);
        const to = uci.slice(2, 4);
        const promotion = uci.length > 4 ? uci.slice(4) : undefined;
        // chess.js throws rather than returning null on an illegal move, so
        // this is inside the try: a UCI left over from a position the attempt
        // has already moved past must be dropped, never forced onto the board.
        const moveResult = chess.move({from, to, promotion});
        if (!moveResult) {
          return;
        }
        appendMove({
          san: moveResult.san,
          fen: chess.fen(),
          lastFen: currentFen,
          byStudent: false,
        });
      } catch (e) {
        console.warn('Practice mode: failed to play engine move:', e);
      }
    }, replyDelayMs);
    return () => clearTimeout(timer);
  }, [
    engineToMove,
    engineEnabled,
    hasOpponentMove,
    currentFen,
    appendMove,
    replyDelayMs,
  ]);

  // --- attempt controls --------------------------------------------------
  const reset = useCallback(() => {
    attemptMovesRef.current = [];
    setAttemptMoves([]);
    setVerdict({status: 'idle', message: ''});
    setTakebackCount(0);
    setAttemptNonce(prev => prev + 1);
    setLatchedOutcome(null);
    pendingEvalRef.current = null;
    setmoveSelected({
      move: '',
      id: 'start',
      fen: resolvedStartFen,
      lastFen: resolvedStartFen,
    });
  }, [resolvedStartFen, setmoveSelected]);

  /** How many take backs are left, or null when the coach set no limit. */
  const takebacksRemaining =
    takebacksAllowed == null
      ? null
      : Math.max(0, takebacksAllowed - takebackCount);
  const canTakeBack =
    outcome.status === 'ongoing' &&
    attemptMoves.length > 0 &&
    (takebacksRemaining == null || takebacksRemaining > 0);

  /**
   * Undo back to the student's previous turn (their move plus the engine's
   * reply, when there is one). Counted and surfaced, rather than being the
   * silent side effect of pressing the back arrow that it used to be.
   */
  const takeBack = useCallback(() => {
    // Was `outcome.status !== 'ongoing' && attemptMoves.length === 0`, whose
    // `&&` let a FINISHED game be taken back as long as it had moves on the
    // board - which un-finished a recorded result and, against a `mateIn`
    // budget, let a student rewind until the mate happened to fit. Both
    // conditions have to hold on their own.
    if (!canTakeBack) {
      return;
    }
    const prev = attemptMovesRef.current;
    if (!prev.length) {
      return;
    }
    let cut = prev.length - 1;
    // Drop the engine's reply too, so the student lands back on their move.
    if (!prev[cut].byStudent && cut > 0) {
      cut -= 1;
    }
    const next = prev.slice(0, cut);
    attemptMovesRef.current = next;
    setAttemptMoves(next);
    const newTip = next.length ? next[next.length - 1] : null;
    setmoveSelected(
      newTip || {
        move: '',
        id: 'start',
        fen: resolvedStartFen,
        lastFen: resolvedStartFen,
      },
    );
    setVerdict({status: 'idle', message: ''});
    pendingEvalRef.current = null;
    setTakebackCount(n => n + 1);
  }, [canTakeBack, resolvedStartFen, setmoveSelected]);

  /** Select a move from the "Your line" list without leaving the attempt. */
  const selectAttemptMove = useCallback(
    (node: any) => {
      setmoveSelected({...node});
    },
    [setmoveSelected],
  );

  // An attempt always starts from the chapter's own starting position.
  //
  // Without this, entering practice mode left the board wherever the
  // selection happened to be - typically the chapter's first AUTHORED move,
  // since that's what the move list selects on load. The attempt itself was
  // correctly empty ("Move 0"), so the board and the attempt disagreed about
  // whose turn it was, and playStudentMove refused every single move as
  // "not the student's colour". The student simply could not move.
  //
  // Only fires while the attempt is empty and the selection isn't already a
  // practice node, so it can't fight the student browsing back through
  // "Your line".
  useEffect(() => {
    if (!isActive || attemptMoves.length > 0) {
      return;
    }
    if (moveSelected?.id === 'start' || moveSelected?.isPracticeMove) {
      return;
    }
    setmoveSelected({
      move: '',
      id: 'start',
      fen: resolvedStartFen,
      lastFen: resolvedStartFen,
    });
  }, [
    isActive,
    attemptMoves.length,
    moveSelected?.id,
    moveSelected?.isPracticeMove,
    resolvedStartFen,
    setmoveSelected,
  ]);

  // A new session (chapter switch, or a Sandbox re-arm) is a fresh attempt -
  // except latchedOutcome, which seeds from initialOutcome (the viewer's
  // prior result on this chapter, if any) rather than always clearing to
  // 'ongoing', so a previously-completed Exercise stays showing complete.
  useEffect(() => {
    attemptMovesRef.current = [];
    setAttemptMoves([]);
    setVerdict({status: 'idle', message: ''});
    setTakebackCount(0);
    setIsEngineThinking(false);
    setLatchedOutcome(initialOutcomeRef.current ?? null);
    pendingEvalRef.current = null;
  }, [sessionKey]);

  /**
   * Whether a piece standing on `fromSquare` may be picked up at all.
   *
   * This is the PREVENTIVE half of the rule `playStudentMove` enforces after
   * the fact, and it exists because "after the fact" turned out to be too
   * late. The board keeps its own chess.js instance and advances it the moment
   * a legal drop lands; the screen's only way to reject a move was to re-set
   * its `moveSelected` state, which changes no prop the board actually watches
   * (it compares the `position` and `lastMoveFen` STRINGS), so the board never
   * resynced. The student could drag the computer's pieces and the move stuck -
   * and from then on the board was playing a different game from the attempt,
   * feeding back a `lastFen` that made `replayAttempt` fail and the outcome
   * unwinnable.
   *
   * Wired to the board's `onPieceDropExtension`, which runs BEFORE it mutates
   * anything. See Screens/FunChallenges/docs/pdb_flow.md, where the same board
   * contract is used for the same reason.
   */
  const canStudentMoveFrom = useCallback(
    (fromSquare: string): boolean => {
      if (!isActive) {
        return true;
      }
      if (outcome.status !== 'ongoing' || !isAtTip) {
        return false;
      }
      const fen = sanitizeFen(currentFen);
      if (fen.split(' ')[1] !== studentColor) {
        return false;
      }
      try {
        const piece = new Chess(fen).get(fromSquare as any);
        return Boolean(piece) && piece.color === studentColor;
      } catch (e) {
        return false;
      }
    },
    [isActive, outcome.status, isAtTip, currentFen, studentColor],
  );

  return {
    attemptMoves,
    studentSans,
    studentMoveCount,
    takebackCount,
    takebacksRemaining,
    canTakeBack,
    attemptNonce,
    outcome,
    verdict,
    isEngineThinking,
    isAtTip,
    currentFen,
    reachedEndOfAuthoredLine,
    canStudentMoveFrom,
    playStudentMove,
    ingestEngineResult,
    takeBack,
    reset,
    selectAttemptMove,
  };
}
